import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom"
import axios from "../../../utils/cmsAxios";
import SortableTable from "../CommonComponents/SortableTable";
import PeriodPicker, { toKstIsoRange } from "../CommonComponents/PeriodPicker";
// import { FiPlus } from "react-icons/fi";
import { Calendar } from "lucide-react";

const PAGE_SIZE = 10;
const API = process.env.REACT_APP_FASTAPI_ADS_URL;

const CmsInquiry = ({ type  = "admin" }) => {
    const navigate = useNavigate();

    // const isAdmin = type === "admin";
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // 검색/필터/페이지
    const [field, setField] = useState("all");      // all | title | content | writer
    const [keyword, setKeyword] = useState("");
    const [statusFilter, setStatusFilter] = useState("All"); // All | pending | answered
    const [categoryFilter, setCategoryFilter] = useState("all");
    // const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);

    // 상세 패널(우측) 상태
    // const [openDetail, setOpenDetail] = useState(false);
    // const [current, setCurrent] = useState(null); // 정규화된 단일 문의
    // const [answerText] = useState("");
    // const [submitAnswer, setSubmitting] = useState(false);

    // 날짜
    const [showJoinPicker, setShowJoinPicker] = useState(false);
    const [joinMode, setJoinMode] = useState("single"); // 'single' | 'range'
    const [joinSingle, setJoinSingle] = useState(null); // Date | null
    const [joinRange, setJoinRange] = useState({ from: null, to: null }); // {from,to}    

    const [applied, setApplied] = useState({
        field: "all",
        keyword: "",
        statusFilter: "all",
        categoryFilter: "all",
        joinStart: "",
        joinEnd: "",
    });

    const getCurrentJoinParams = useCallback(() => {
        if (joinMode === "single" && joinSingle) {
            return toKstIsoRange(joinSingle, joinSingle);
        }
        if (joinMode === "range" && joinRange.from) {
            return toKstIsoRange(joinRange.from, joinRange.to || joinRange.from);
        }
        return {};
    }, [joinMode, joinSingle, joinRange]);

    // 데이터 불러오기
    const fetchInquiries = useCallback(async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const url = `${API}/help/`;
            const res = await axios.get(url, {
                params: { limit: 200, offset: 0 },
                headers: { Accept: "application/json" },
                timeout: 15000,
                withCredentials: false,
                validateStatus: () => true
            });

            const ct = res.headers?.["content-type"] || "";
            const looksHtml = typeof res.data === "string" || ct.includes("text/html");

            if (res.status >= 200 && res.status < 300 && !looksHtml && Array.isArray(res.data)) {
                setList(res.data.map(normalizeInquiry));
                return;
            }

            console.error("[CmsInquiry] Unexpected response", {
                status: res.status,
                contentType: ct,
                sample: String(res.data).slice(0, 120)
            });
            setErrorMsg("목록을 불러오지 못했습니다. (응답이 JSON이 아님)");
            setList([]);
        } catch (err) {
            console.error("[CmsInquiry] fetch error:", err);
            if (err.code === "ECONNABORTED") {
                setErrorMsg("요청이 시간 초과되었습니다.");
            } else if (err.message?.includes("Network Error")) {
                setErrorMsg("네트워크 오류가 발생했습니다. 서버 접근이 차단되었을 수 있습니다.");
            } else {
                setErrorMsg("목록을 불러오지 못했습니다.");
            }
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInquiries();
    }, [fetchInquiries]);

    // 정규화 유틸(백엔드 필드명이 조금 달라도 견딤)
    const normalizeInquiry = (raw) => {
        const id =
            raw.id ?? raw.help_id ?? raw.inquiry_id ?? raw.ticket_id ?? raw.uuid;
        const category =
            raw.category ?? raw.category_name ?? raw.type ?? "미분류";
        const name = raw.name ?? raw.user_name ?? "";
        const email = raw.email ?? "";
        const phone = raw.phone ?? "";
        const contentRaw = String(raw.content ?? raw.message ?? raw.body ?? "");
        let title = raw.title ?? raw.subject ?? "";
        let content = contentRaw;                 // 상세용 전체 본문
        let content_preview = contentRaw;         // 목록 미리보기

        if (!title) {
            const trimmed = contentRaw.replace(/\r\n/g, "\n").trimStart();
            const [firstLine, ...rest] = trimmed.split("\n");
            title = (firstLine || "").slice(0, 30) || "(제목 없음)";
            content_preview = rest.join("\n").trimStart() || "";
        }
        const created_at =
            raw.created_at ?? raw.createdAt ?? raw.inserted_at ?? raw.created ?? null;
        const answer = raw.answer ?? raw.reply ?? "";
        const answered_at = raw.answered_at ?? raw.reply_at ?? null;
        const status =
            raw.status ?? (answer && String(answer).trim() ? "answered" : "pending");
        // 첨부 처리: 배열 또는 file1_url, file2_url...
        const fromArray = Array.isArray(raw.attachments) ? raw.attachments : [];
        const fromKeys = ["attachment1", "attachment2", "attachment3"]
            .map((k) => raw[k])
            .filter(Boolean)
            .map((url) => ({
                url,
                filename: String(url).split("/").pop(),
            }));
        const attachments = [...fromArray, ...fromKeys].map((a, i) => ({
            url: a.url ?? a,
            filename: a.filename ?? (String(a.url ?? a).split("/").pop() || `file_${i + 1}`),
        }));
        
        return {
            id,
            category,
            name,
            email,
            phone,
            title,
            content,
            content_preview,
            created_at,
            status,
            answer,
            answered_at,
            attachments,
            _raw: raw,
        };
    };

    // 카테고리 옵션(목록 기반)
    const categoryOptions = useMemo(() => {
        const set = new Set(list.map((x) => x.category || "미분류"));
        return ["all", ...Array.from(set)];
    }, [list]);

    // 필터 + 검색
    const filteredList = useMemo(() => {
        const { field: f, keyword: kw, statusFilter: st, categoryFilter: cf, joinStart, joinEnd } = applied;
        // const q = searchText.trim().toLowerCase();
        const q = (kw || "").trim().toLowerCase();
        const hasRange = !!(joinStart && joinEnd);
        const startMs = hasRange ? new Date(joinStart).getTime() : NaN;
        const endMs = hasRange ? new Date(joinEnd).getTime() : NaN;

        return list.filter((x) => {
            // const okStatus = statusFilter === "All" ? true : x.status === statusFilter;
            // if (!okStatus) return false;
            // 상태
            if (st !== "all" && x.status !== st) return false;
            // 카테고리
            if (cf !== "all" && (x.category || "미분류") !== cf) return false;
            // 날짜(등록일 기준)
            if (hasRange) {
                const t = new Date(x.created_at ?? 0).getTime();
                if (!Number.isFinite(t) || t < startMs || t > endMs) return false;
            }
            if (!q) return true;
            // const hay = `${x.category} ${x.name} ${x.email} ${x.phone} ${x.title} ${x.content}`.toLowerCase();
            // return hay.includes(q);
            const hayAll = `${x.category} ${x.name} ${x.email} ${x.phone} ${x.title} ${x.content} ${x.answered_by}`.toLowerCase();
            const hayName = `${x.name} ${x.email} ${x.phone}`.toLowerCase();
            const hayContent = `${x.title} ${x.content}`.toLowerCase();
            const hayWriter = `${x.answered_by}`.toLowerCase();

            if (f === "all") return hayAll.includes(q);
            if (f === "name") return hayName.includes(q);
            if (f === "content") return hayContent.includes(q);
            if (f === "writer") return hayWriter.includes(q);
            return true;
        });
    }, [list, applied]);

    // 페이지네이션
    const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
    const pagedList = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredList.slice(start, start + PAGE_SIZE);
    }, [filteredList, page]);

    // 각 행에 전역(필터 기준) 역순 번호 주입
    const tableData = useMemo(() => {
        const base = (page - 1) * PAGE_SIZE;
        return pagedList.map((row, idx) => ({
            ...row,
            rowNo: filteredList.length - (base + idx),
        }));
    }, [pagedList, filteredList.length, page]);

    useEffect(() => {
        setPage(1);
    }, [fetchInquiries]);

    // 행 클릭 → 상세 열기
    // const openRow = async (row) => {
    //     setOpenDetail(true);
    //     setCurrent(row);
    //     setAnswerText(row.answer || "");
    //     try {
    //         const res = await axios.get(`${API}/help/${row.id}`, {
    //             headers: { Accept: "application/json" },
    //             timeout: 15000,
    //             validateStatus: () => true
    //         });
    //         const ct = res.headers?.["content-type"] || "";
    //         const looksHtml = typeof res.data === "string" || ct.includes("text/html");
    //         if (res.status >= 200 && res.status < 300 && !looksHtml) {
    //             const detailed = normalizeInquiry(res.data);
    //             setCurrent(detailed);
    //             setAnswerText(detailed.answer || "");
    //         } else {
    //             console.error("[CmsInquiry] detail unexpected response", res.status, res.data);
    //         }
    //     } catch (err) {
    //         console.error("[CmsInquiry] openRow detail fetch error:", err);
    //     }
    // };
    const openRow = (row) => {
        if (!row?.id) return;
        navigate(`/cms/inquiry/${row.id}`);
    }

    // (임시) 답변 등록/수정은 관리자 기능이므로 비활성화
    // 필요한 경우 주석 해제하고 API 절대주소 사용:
    // const submitAnswer = async () => {
    //     if (!isAdmin || !current) return; // 관리자 외 작성/수정 금지
    //     try {
    //         setSubmitting(true);
    //         const url = `${API}/help/${current.id}/status`;
    //         const res = await axios.patch(
    //             url,
    //             { status: "answered", answer: answerText },
    //             { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
    //         );
    //         if (res.status < 200 || res.status >= 300) {
    //             alert("답변 저장 실패(서버 응답 오류).");
    //             return;
    //         }
    //         // 저장 직후 상세 재조회
    //         let updated = null;
    //         try {
    //             const res2 = await axios.get(`${API}/help/${current.id}`, {
    //                 headers: { Accept: "application/json" },
    //                 timeout: 15000,
    //                 validateStatus: () => true
    //             });
    //             const ct2 = res2.headers?.["content-type"] || "";
    //             const looksHtml2 = typeof res2.data === "string" || ct2.includes("text/html");
    //             if (res2.status >= 200 && res2.status < 300 && !looksHtml2) {
    //                 updated = normalizeInquiry(res2.data);
    //             }
    //         } catch (e) {
    //             console.error("[CmsInquiry] refetch after patch error:", e);
    //         }
    //         if (!updated) {
    //             updated = normalizeInquiry({
    //                 ...(current._raw || {}),
    //                 answer: answerText,
    //                 status: "answered",
    //                 answered_at: new Date().toISOString()
    //             });
    //         }
    //         // const updated = normalizeInquiry({
    //         //     ...(current._raw || {}),
    //         //     answer: answerText,
    //         //     status: "answered",
    //         //     answered_at: new Date().toISOString(),
    //         // });
    //         setCurrent(updated);
    //         setList((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    //         alert("답변이 저장되었습니다.");
    //     } catch (err) {
    //         // console.error("[CmsInquiry] submitAnswer error:", err);
    //         alert("답변 저장에 실패했습니다. (콘솔을 확인하세요)");
    //     } finally {
    //         setSubmitting(false);
    //     }
    // };

    // 날짜 포맷
    // const formatDate = useCallback((d) => {
    //     if (!d) return "-";
    //     const dt = typeof d === "string" ? new Date(d) : d;
    //     if (Number.isNaN(dt.getTime())) return "-";
    //     const y = dt.getFullYear();
    //     const m = String(dt.getMonth() + 1).padStart(2, "0");
    //     const day = String(dt.getDate()).padStart(2, "0");
    //     const hh = String(dt.getHours()).padStart(2, "0");
    //     const mm = String(dt.getMinutes()).padStart(2, "0");
    //     return `${y}-${m}-${day} ${hh}:${mm}`;
    // }, []);

    const withDateTooltip = useCallback((v) => {
            if (!v) return "-";
            const d = new Date(v);
            if (Number.isNaN(d.getTime())) return "-";
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            const hh = String(d.getHours()).padStart(2, "0");
            const mm = String(d.getMinutes()).padStart(2, "0");
            const ss = String(d.getSeconds()).padStart(2, "0");
            const tip = `${y}.${m}.${dd} ${hh}:${mm}:${ss}`;
            return <time dateTime={d.toISOString()} title={tip}>{`${y}-${m}-${dd}`}</time>;
        }, []);

    const columns = useMemo(() => ([
        // { key: "rowNo", header: "번호", type: "number" },
        { key: "status", header: "처리상태", type: "string", render: (v) => (
            <span
                className={
                    "inline-flex items-center rounded-xl px-3 py-1 text-xs " +
                    (v === "answered" ? "bg-gray-100 text-gray-700" : "bg-violet-500/10 text-violet-500")
                }
            >
                {v === "answered" ? "완료" : "대기"}
            </span>
        )},
        { key: "category", header: "문의 종류", type: "string", align: "center" },
        { key: "name", header: "이름", type: "string" },
        { key: "email", header: "이메일", type: "string" },
        { key: "phone", header: "휴대폰 번호", type: "string", align: "center" },
        // { key: "title", header: "내용", type: "string", render: (v) => (
        //     <div className="line-clamp-1" title={v}>{v}</div>)
        // },
        { key: "created_at", header: "등록일시", type: "date", render: withDateTooltip, align: "center" },
        { key: "admin", header: "답변 등록자", type: "string", render: () => "admin", align: "center"},
        { key: "answered_at", header: "답변 등록 일시", type: "date", render: withDateTooltip, align: "center" },
    ]), [withDateTooltip]);

    const goPrev = () => setPage((p) => Math.max(1, p - 1));
    const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

    const handleSearch = () => {
        const cur = getCurrentJoinParams();
        setApplied({
            field,
            keyword,
            statusFilter,
            categoryFilter,
            joinStart: cur.join_start || "",
            joinEnd: cur.join_end || "",
        });
    };

    const handleReset = () => {
        setField("all");
        setKeyword("");
        setStatusFilter("all");
        setCategoryFilter("all");
        setShowJoinPicker(false);
        setJoinMode("single");
        setJoinSingle(null);
        setJoinRange({ from: null, to: null });
        setApplied({
            field: "all",
            keyword: "",
            statusFilter: "all",
            categoryFilter: "all",
            joinStart: "",
            joinEnd: "",
        });
        fetchInquiries();
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_40px_80px_-24px_rgba(0,0,0,0.12)] overflow-hidden">
                <div className="flex">
                    {/* Right content */}
                    <div className="flex-1 min-w-0 bg-neutral-50/70">

                        <main className="pt-12 flex flex-col gap-4 p-4 overflow-x-hidden w-full">
                            <section className="p-4">
                                <div className="flex justify-between items-center pb-4">
                                    <h2 className="text-xl font-semibold mb-3">1:1 문의</h2>
                                </div>

                                {/* 에러 표시 */}
                                {errorMsg && (
                                    <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
                                        {errorMsg}
                                    </div>
                                )}

                                {/* 검색 + 필터 */}
                                <div className="rounded-lg bg-white p-3">
                                    <div className="flex flex-wrap items-end gap-6">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">검색</span>
                                            <div className="flex items-center gap-2">
                                                <select
                                                    value={field}
                                                    onChange={(e) => setField(e.target.value)}
                                                    className="h-9 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                                >
                                                    <option value="all">전체</option>
                                                    <option value="name">이름</option>
                                                    <option value="content">내용</option>
                                                    <option value="writer">답변등록자</option>
                                                </select>
                                                <input
                                                    value={keyword}
                                                    onChange={(e) => setKeyword(e.target.value)}
                                                    type="text"
                                                    placeholder="검색어를 입력하세요"
                                                    className="h-9 w-52 rounded-md border border-neutral-300 bg-white px-3 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">처리 상태</span>
                                            <select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                className="h-9 w-24 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                            >
                                                <option value="all">전체</option>
                                                <option value="pending">대기</option>
                                                <option value="answered">완료</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">문의 종류</span>
                                            <select
                                                value={categoryFilter}
                                                onChange={(e) => setCategoryFilter(e.target.value)}
                                                className="h-9 w-24 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                            >
                                                {categoryOptions.map((opt) => (
                                                    <option key={opt} value={opt}>
                                                        {opt === "all" ? "전체" : opt}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* <div className="hidden h-6 w-px bg-neutral-200 md:block" /> */}
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-neutral-500">등록(수정)일</span>
                                            <button
                                                type="button"
                                                onClick={() => setShowJoinPicker(true)}
                                                className="h-9 w-[230px] inline-flex items-center justify-between gap-2
                                                        rounded-md border border-neutral-300 bg-white px-2 text-sm whitespace-nowrap"
                                            >
                                                <span className="truncate">
                                                    {(() => {
                                                        const cur = getCurrentJoinParams();
                                                        if (cur.join_start && cur.join_end) {
                                                            const s = new Date(cur.join_start);
                                                            const e = new Date(cur.join_end);
                                                            const fmt = (d) =>
                                                            `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                                                            return `${fmt(s)} ~ ${fmt(e)}`;
                                                        }
                                                        return "-";
                                                    })()}
                                                </span>
                                                <Calendar />
                                            </button>

                                            {showJoinPicker && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 bg-black/30 z-[40]"
                                                        onClick={() => setShowJoinPicker(false)}
                                                    />
                                                    <div className="absolute z-[50] mt-2 rounded-lg border bg-white shadow-lg p-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <select
                                                                value={joinMode}
                                                                onChange={(e) => setJoinMode(e.target.value)}
                                                                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-sm"
                                                            >
                                                            <option value="single">하루</option>
                                                            <option value="range">기간</option>
                                                            </select>
                                                        </div>
                                                    
                                                        <PeriodPicker
                                                            key={`pp-${joinMode}-${joinSingle ? +joinSingle : 0}-${joinRange.from ? +joinRange.from : 0}-${joinRange.to ? +joinRange.to : 0}`}
                                                            mode={joinMode}
                                                            value={joinMode === "single" ? joinSingle : joinRange}
                                                            onChange={(val) => {
                                                                if (joinMode === "single") {setJoinSingle(val || null);}
                                                                else {setJoinRange(val ?? { from: null, to: null });}
                                                                }}
                                                            onConfirm={() => setShowJoinPicker(false)}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-3 flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleReset}
                                            className="h-9 px-3 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                                        >
                                            초기화
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleSearch()}
                                            className="h-9 px-3 rounded-md bg-[#7864F9] text-white hover:bg-indigo-700 shadow-sm"
                                        >
                                            검색
                                        </button>
                                    </div>
                                </div>
                                {/* <div className="flex items-center justify-between mb-4 gap-3">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            placeholder="검색어 (이름, 이메일, 연락처, 내용)"
                                            className="w-[28vw] h-10 px-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-xl">
                                            <SlMagnifier />
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {["All", "pending", "answered"].map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => setStatusFilter(s)}
                                                className={`px-3 py-1.5 rounded-full border text-sm transition ${
                                                    statusFilter === s
                                                        ? "bg-blue-600 text-white border-blue-600"
                                                        : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                                                }`}
                                            >
                                                {s === "All" ? "전체" : s === "pending" ? "대기" : "완료"}
                                            </button>
                                        ))}
                                    </div>
                                </div> */}

                                <div className="text-sm text-neutral-600 py-3">
                                    총 <span className="text-violet-500">{filteredList.length} 건</span>
                                </div>

                                {/* 테이블 */}
                                <div className="cms-inquiry-table overflow-hidden rounded-xl border border-gray-200 bg-white p-4 text-sm">
                                    {loading ? (
                                        <div className="px-4 py-10 text-center text-gray-500">불러오는 중…</div>
                                    ) : tableData.length === 0 ? (
                                        <div className="px-4 py-10 text-center text-gray-500">문의가 없습니다.</div>
                                    ) : (
                                        <SortableTable
                                            data={tableData}
                                            columns={columns}
                                            defaultSort={{ key: "created_at", dir: "desc" }}
                                            rowKey="id"
                                            onRowClick={openRow}
                                            // rowClassName={() => "border-t border-gray-100"}
                                            tableClassName="min-w-full border-separate border-spacing-0"
                                            headerCellClassName="cms-inquiry-table-header bg-neutral-50 text-black/50 first:rounded-tl-xl last:rounded-tr-xl text-sm"
                                            bodyClassName="cms-inquiry-table-body text-sm text-black/50"
                                            columnWidths={["110px","120px","80px","200px","150px","120px","130px","150px"]}
                                        />
                                    )}
                                </div>

                                {/* 페이지네이션 */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-2 mt-4">
                                        <button
                                            className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                                            onClick={goPrev}
                                            disabled={page === 1}
                                        >
                                            이전
                                        </button>
                                        {Array.from({ length: totalPages }).map((_, i) => (
                                            <button
                                                key={i}
                                                className={`w-8 h-8 rounded-lg text-sm border transition ${
                                                    page === i + 1
                                                        ? "bg-blue-600 text-white border-blue-600"
                                                        : "border-gray-200 hover:bg-gray-100"
                                                }`}
                                                onClick={() => setPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                        <button
                                            className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                                            onClick={goNext}
                                            disabled={page === totalPages}
                                        >
                                            다음
                                        </button>
                                    </div>
                                )}
                            </section>
                        </main>
                    </div>

                    {/* 우측 상세 패널 */}
                    {/* {openDetail && current && (
                        <div className="w-[40rem] max-w-[90vw] border-l bg-neutral-50 h-[calc(100vh-2rem)] mt-4 mr-4 rounded-xl shadow-xl fixed right-0 top-0 overflow-hidden flex flex-col">
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <h3 className="text-2xl font-extrabold">1:1 문의 {">"} 상세</h3>
                                <button
                                    onClick={() => setOpenDetail(false)}
                                    className="px-3 py-1.5 rounded-md border hover:bg-gray-50"
                                >
                                    닫기
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                <div className="text-lg text-black font-extrabold">회원 기본 정보</div>
                                <div className="border rounded-lg overflow-hidden">
                                    <table className="w-full table-fixed text-sm border-collapse">
                                        <colgroup>
                                            <col className="w-28 md:w-32" />
                                            <col />
                                            <col className="w-28 md:w-32" />
                                            <col />
                                        </colgroup>
                                        <tbody className="divide-y divide-gray-200">
                                            <tr>
                                                <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2">
                                                    이름
                                                </th>
                                                <td className="px-4 py-2 border-l bg-white border-gray-200">
                                                    {current.name || "-"}
                                                </td>
                                                <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2 border-l border-gray-200">
                                                    이메일
                                                </th>
                                                <td className="px-4 py-2 border-l bg-white border-gray-200 break-all">
                                                    {current.email || "-"}
                                                </td>
                                            </tr>
                                            <tr className="border-gray-200">
                                                <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2">
                                                    휴대폰 번호
                                                </th>
                                                <td className="px-4 py-2 bg-white border-l border-gray-200">
                                                    {current.phone || "-"}
                                                </td>
                                                <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2 border-l border-gray-200">
                                                    등록일시
                                                </th>
                                                <td className="px-4 py-2 bg-white border-l border-gray-200">
                                                    {formatDate(current.created_at)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                {/* 내용 
                                <div className="text-lg text-black font-extrabold mb-1">문의 내용</div>
                                <table className="ring-2 rounded-md ring-gray-200 border-gray-200 border-collapse bg-white">
                                    <tr className="border-b border-gray-200">
                                        <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-3 whitespace-nowrap border-r border-gray-200">
                                            문의 종류
                                        </th>
                                        <td className="px-4 py-2 w-full bg-white text-gray-800 border-r border-gray-200">
                                            {current.category || "-"}
                                        </td>
                                    </tr>
                                    <tr className="border-b border-gray-200">
                                        <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-3 whitespace-nowrap border-r border-gray-200 align-top">
                                            내용
                                        </th>
                                        <td colSpan={3} className="p-4">
                                            <div className="rounded-lg border border-gray-200 bg-white p-4 min-h-[12rem] whitespace-pre-line break-words">
                                                {current.content || "-"}
                                            </div>
                                        </td>
                                    </tr>
                                    {/* 첨부
                                    <tr className="border-b border-gray-200">
                                        <th scope="row" className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-3 whitespace-nowrap border-r border-gray-200 align-top">
                                            첨부 파일
                                        </th>
                                        <td colSpan={3} className="px-4 py-3">
                                            {current.attachments?.length ? (
                                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                                    {current.attachments.map((a, i) => (
                                                        <a
                                                            key={i}
                                                            href={a.url}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            className="text-blue-600 hover:underline break-all"
                                                        >
                                                            {a.filename}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">첨부 없음</span>
                                            )}
                                        </td>
                                    </tr>
                                </table>

                                {/* 관리자 답변 UI (임시 비활성화) 
                                {isAdmin ? (                 
                                    <div className="border-t">
                                        <div className="text-2xl text-black font-extrabold pt-2">답변</div>
                                        <div className="grid grid-cols-12 gap-4 pt-3">
                                            <div className="col-span-12 md:col-span-4">
                                                <label className="block text-m text-gray-500 pt-1">등록(수정)자</label>
                                                <div className="w-full h-9 px-3 flex items-center rounded-md border border-gray-200 bg-white text-gray-500">
                                                    {isAdmin ? "Admin" : (current.name || "-")}
                                                </div>
                                            </div>
                                            
                                            <div className="col-span-12 md:col-span-4">
                                                <label className="block text-m text-gray-500 pt-1">등록(수정)일시</label>
                                                <div className="w-full h-9 px-3 flex items-center rounded-md border border-gray-200 bg-white text-gray-500">
                                                    {current.answered_at ? formatDate(current.answered_at) : formatDate(new Date())}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="text-lg text-black font-extrabold pt-6">답변 내용</div>                                    
                                        <textarea
                                            className="w-full h-40 px-3 py-2 border rounded-lg"
                                            value={answerText}
                                            onChange={(e) => setAnswerText(e.target.value)}
                                            placeholder="고객에게 전달될 답변을 작성하세요."
                                        />
                                        <div className="flex justify-end gap-2 pt-2">
                                            <button
                                                className="px-4 h-10 rounded-lg border hover:bg-gray-50"
                                                onClick={() => setAnswerText(current.answer || "")}
                                                disabled={submitting}
                                            >
                                                원래대로
                                            </button>
                                            <button
                                                className="px-4 h-10 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                                onClick={submitAnswer}
                                                disabled={submitting || !answerText.trim()}
                                            >
                                                {submitting ? "저장 중..." : "답변 등록"}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border-t">
                                        <div className="text-sm text-gray-500 mb-2">답변</div>
                                        <div className="whitespace-pre-line min-h-[4rem]">
                                            {current.answer?.trim() ? current.answer : "아직 답변이 준비되지 않았습니다."}
                                        </div>
                                    </div>
                                )}
                            </div>
                        
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    );
};

export default CmsInquiry;
